import { createClient } from '@/lib/supabase/client';
import { type Dictionary, type Lang, normalise } from '@/lib/i18n';

/**
 * Corrections to the bundled translation.
 *
 * The Lithuanian itself ships in the repository — see
 * `src/lib/translate/dictionary.ts`. This table holds only what somebody has
 * since typed over it in the dashboard: a headline reworded, a term the house
 * prefers, a phrase that reads better on the page than it did in a list.
 *
 * Kept as data rather than in the bundle because the point of it is to be
 * changed without a deploy. It is the site's own database, not a service: with
 * it unreachable every page is still fully translated, just without the
 * corrections.
 *
 * Keyed by a hash of the English text, because the key has to be indexable and
 * a paragraph is too long for a btree index.
 */

export const TRANSLATIONS_TABLE = 'translations';

export interface TranslationRow {
  id: string;
  lang: Lang;
  source: string;
  /** What the bundled dictionary says. Not stored — filled in by the editor. */
  bundled: string;
  /** What a person typed instead. Wins wherever it exists. */
  human: string;
  updatedAt: string;
}

/** Stable, short, and computable in the browser and on the server alike. */
export function sourceKey(text: string): string {
  const value = normalise(text);
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < value.length; i += 1) {
    const c = value.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193);
    h2 = Math.imul(h2 ^ c, 0x85ebca6b);
  }
  const hex = (n: number) => (n >>> 0).toString(16).padStart(8, '0');
  return `${hex(h1)}${hex(h2)}${value.length.toString(36)}`;
}

/**
 * Every correction for a language, as a plain lookup.
 *
 * Read whole rather than per-string: there are at most a few hundred, one
 * request is cheaper than hundreds, and the page is already readable from the
 * bundle while this is in flight.
 */
export async function fetchOverrides(lang: Lang): Promise<Dictionary> {
  if (lang === 'en') return {};
  const supabase = createClient();
  if (!supabase) return {};

  const overrides: Dictionary = {};
  const pageSize = 1000;
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await supabase
      .from(TRANSLATIONS_TABLE)
      .select('source, human')
      .eq('lang', lang)
      .neq('human', '')
      .range(from, from + pageSize - 1);
    if (error || !data?.length) break;
    for (const row of data) {
      const source = normalise(String((row as { source?: string }).source ?? ''));
      const human = String((row as { human?: string }).human ?? '').trim();
      if (source && human) overrides[source] = human;
    }
    if (data.length < pageSize) break;
  }
  return overrides;
}

/** The stored corrections, for the dashboard's editor. */
export async function fetchOverrideRows(lang: Lang): Promise<Record<string, TranslationRow>> {
  const supabase = createClient();
  if (!supabase) return {};
  const { data, error } = await supabase
    .from(TRANSLATIONS_TABLE)
    .select('id, source, human, updated_at')
    .eq('lang', lang)
    .neq('human', '')
    .limit(5000);
  if (error || !data) return {};

  const rows: Record<string, TranslationRow> = {};
  for (const row of data as Record<string, unknown>[]) {
    const source = normalise(String(row.source ?? ''));
    if (!source) continue;
    rows[source] = {
      id: String(row.id ?? ''),
      lang,
      source,
      bundled: '',
      human: String(row.human ?? ''),
      updatedAt: String(row.updated_at ?? ''),
    };
  }
  return rows;
}

/** Saves a person's wording for one string. Requires a signed-in session. */
export async function saveOverride(lang: Lang, source: string, human: string): Promise<boolean> {
  const supabase = createClient();
  if (!supabase) return false;
  const value = normalise(source);
  const { error } = await supabase.from(TRANSLATIONS_TABLE).upsert(
    {
      lang,
      source: value,
      source_key: sourceKey(value),
      human: human.trim(),
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'lang,source_key' }
  );
  return !error;
}

/** Drops a correction, so the bundled translation applies again. */
export async function clearOverride(lang: Lang, source: string): Promise<boolean> {
  const supabase = createClient();
  if (!supabase) return false;
  const { error } = await supabase
    .from(TRANSLATIONS_TABLE)
    .delete()
    .eq('lang', lang)
    .eq('source_key', sourceKey(source));
  return !error;
}
